import type {ReactElement, RefObject} from 'react';
import {Fragment, useContext, useEffect, useRef, useState} from 'react';
import {
  useAccountIdsToPublishTo,
  usePublishingParams,
  usePublishingStatusForAllAccounts,
  useSocialMediaWizardAccounts,
  useSocialMediaWizardPost,
  useSocialPostPublishingAction,
  useWizardStep,
} from '@Components/social-media/social-media.hooks';
import {
  useGraphicsReadiness,
  useNumParentAccountsConnected,
  usePostId,
  useShowReviewRequestOnPublishSettle,
  useSocialMediaPublishPhase,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {SocialMediaPublishPhase} from '@Libraries/social-media-publish-phase.library';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {Text, TextSize} from '@Components/text';
import {AddAccountIcon} from '@Components/social-media/components/add-account-icon';
import {PremiumCrownChip} from '@Components/premium-crown-chip/premium-crown-chip';
import {isEligibleForSocialAccountsUpsellDialog, openSocialAccountsUpsellDialog} from '@Libraries/social-media-account.library';
import {SocialProfileIconSize} from '@Components/social-media/components/social-profile-icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {RailAccountProfileIcon} from './components/rail-account-profile-icon/rail-account-profile-icon';
import type {MediaTypePublishingParams, SocialMediaAccountId} from '@Components/social-media/post.vo';
import {PlatformMediaType, PostState} from '@Components/social-media/post.vo';
import {
  BLURABLE_CLASS_IN_AI_CAPTION_MODE,
  PUBLISH_PARAM_HANDLER_CLASS,
  SocialMediaWizardContentSize,
  SocialMediaWizardStep,
} from '@Components/social-media-wizard/social-media-wizard.types';
import {getKeyForPublishParams, scrollContainerToTop, scrollToPublishParams} from '@Libraries/social-media-wizard-design-library';
import {SocialProfileIconPlaceholder} from '@Components/social-media-wizard/components/social-profile-icon-placeholder';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import type {MaxVisiblePublishingParamInfo} from './social-media-post-details.types';
import {MAX_ACCOUNT_BUTTONS_TO_SHOW, PostDetailsActionMode} from './social-media-post-details.types';
import {openAddMoreAccountsModal} from '@Modals/add-more-accounts-modal/add-more-accounts-modal.library';
import {getPublishingParamsContainerMarginClass, SocialMediaPostPublishParamsWrapper} from '@Components/social-media-wizard/components/social-media-post-publish-params-wrapper';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {SocialPostPlatformSelectionTransitionEvent} from '@Components/social-media/social-media.types';
import {LoadingStates} from '@Utils/loading.util';
import {
  getNonStoryPlatformMediaType,
  hasPublishingTransitionedFromBothToPostOrReelOnly,
  hasPublishingTransitionedFromBothToStoryOnly,
  hasPublishingTransitionedFromPostOrReelOnlyToBoth,
  hasPublishingTransitionedFromStoryOnlyToBoth,
  havePublishingParamsChangedForAccountWithId,
  isPublishingAsAPostOrReelOnly,
  isPublishingAsPost,
  isPublishingAsPostOrReel,
  isPublishingAsReel,
  isPublishingAsStory,
  isPublishingAsStoryOnly,
} from '@Libraries/social-media-params.library';
import publishParamsStyles from '@Components/social-media-wizard/components/social-media-post-publish-params/social-media-post-publish-params.module.scss';
import styles from './social-media-post-details.module.scss';
import {buildAutoCaptionPublishingPayload, createAutoCaptionGenerationFunction, hasAccountsAwaitingAutoCaptions} from '@Libraries/auto-caption-library';
import {useAppDispatch} from '@/hooks';
import type {AutoCaptionGenerationResult} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import {
  setAreCaptionsBeingAutoGenerated,
  updateCommonCaption,
  updateHasCommonCaption,
  updatePublishingParamAllText,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {isUserPremium} from '@Libraries/user.library';
import {SocialMediaTestId} from '@/test-ids/social-media-test-id';

interface SocialMediaPostDetailsProps {
  scrollingContainerRef: RefObject<HTMLDivElement | null>;
  size?: SocialMediaWizardContentSize;
}

export function SocialMediaPostDetails({scrollingContainerRef, size = SocialMediaWizardContentSize.XLARGE}: SocialMediaPostDetailsProps): ReactElement | null {
  const accounts = useSocialMediaWizardAccounts();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const publishingAction = useSocialPostPublishingAction();
  const publishPhase = useSocialMediaPublishPhase();
  useShowReviewRequestOnPublishSettle();
  const graphicsReadiness = useGraphicsReadiness();
  const wizardPost = useSocialMediaWizardPost();
  const wizardStep = useWizardStep();
  const publishingDetails: ReactElement[] = [];
  const selectedAccountsTabs: ReactElement[] = [];
  const publishParamHandlerClass = PUBLISH_PARAM_HANDLER_CLASS;
  const allPublishingParams = usePublishingParams();
  const publishingStatus = usePublishingStatusForAllAccounts();
  const containerQuery = useContext(SocialMediaWizardContainerQueriesContext);
  const {isSmallScreen, isExtraSmallScreen} = containerQuery;
  const postId = usePostId();
  const numParentAccountsConnected = useNumParentAccountsConnected();
  const dispatch = useAppDispatch();
  const isAutoCaptionGenerationInFlightRef = useRef(false);
  const accountIdsWithPublishingParams = accountIdsToPublishTo.filter((accountId) => {
    return allPublishingParams[accountId] !== undefined;
  });

  let firstAccountId: SocialMediaAccountId = 0;
  let firstPublishingParamMediaType = PlatformMediaType.POST;

  if (accountIdsWithPublishingParams.length > 0) {
    [firstAccountId] = accountIdsWithPublishingParams;
    // eslint-disable-next-line
    // @ts-ignore
    firstPublishingParamMediaType =
      getNonStoryPlatformMediaType(allPublishingParams[firstAccountId]) !== null ? getNonStoryPlatformMediaType(allPublishingParams[firstAccountId]) : PlatformMediaType.STORIES;
  }

  const [selectedAccountId, setSelectedAccountId] = useState(firstAccountId);
  const [selectedPublishingParamMediaType, setSelectedPublishingParamMediaType] = useState(firstPublishingParamMediaType);
  const [allPublishingParamState, setAllPublishingParamsState] = useState<Record<SocialMediaAccountId, MediaTypePublishingParams>>({});
  const hasPublishingParamsForSelectedAccount = accountIdsWithPublishingParams.includes(selectedAccountId);

  useEffect(() => {
    if (!firstAccountId || hasPublishingParamsForSelectedAccount) {
      return;
    }

    setSelectedAccountId(firstAccountId);
    setSelectedPublishingParamMediaType(firstPublishingParamMediaType);
  }, [firstAccountId, firstPublishingParamMediaType, hasPublishingParamsForSelectedAccount]);

  const firePlatformSelectionTransitionEvent = (accountId: number, platformSelection: PlatformMediaType): void => {
    window.dispatchEvent(
      new CustomEvent(SocialPostPlatformSelectionTransitionEvent, {
        detail: {
          accountId,
          platformSelection,
        },
      })
    );
  };

  useEffect(() => {
    void generateAutoCaptionsForAccountsAwaitingThem();
  }, [accountIdsToPublishTo, postId]);

  const generateAutoCaptionsForAccountsAwaitingThem = async (): Promise<void> => {
    if (!isUserPremium() || !postId || isAutoCaptionGenerationInFlightRef.current) {
      return;
    }

    if (!hasAccountsAwaitingAutoCaptions(accountIdsToPublishTo, allPublishingParams)) {
      return;
    }

    isAutoCaptionGenerationInFlightRef.current = true;
    try {
      await generateAndApplyAutoCaptions(postId);
    } finally {
      isAutoCaptionGenerationInFlightRef.current = false;
    }
  };

  const generateAndApplyAutoCaptions = async (postIdToGenerateFor: string): Promise<void> => {
    dispatch(setAreCaptionsBeingAutoGenerated(true));

    try {
      const autoCaptionFunction = createAutoCaptionGenerationFunction(getAccountsToPublishTo(), postIdToGenerateFor);
      const response = await autoCaptionFunction();

      if (response) {
        updatePublishingParamsAfterAutoGeneration(response);
      }
    } finally {
      dispatch(setAreCaptionsBeingAutoGenerated(false));
    }
  };

  const getAccountsToPublishTo = (): SocialAccount[] => {
    return accounts.filter((account) => {
      return accountIdsToPublishTo.includes(account.id);
    });
  };

  const updatePublishingParamsAfterAutoGeneration = (result: AutoCaptionGenerationResult): void => {
    const payload = buildAutoCaptionPublishingPayload(result, accountIdsToPublishTo, allPublishingParams);

    if (Object.keys(payload).length === 0) {
      return;
    }

    if (result.hasMultiplePlatformCaptions) {
      dispatch(updateHasCommonCaption(false));
    }

    dispatch(updatePublishingParamAllText(payload));

    if (!result.hasMultiplePlatformCaptions) {
      const [onlyGeneratedCaption] = Object.values(result.captionsByAccountId);
      dispatch(updateCommonCaption(onlyGeneratedCaption.caption));
    }
  };

  useEffect(() => {
    if (Object.keys(allPublishingParamState).length === 0) {
      setAllPublishingParamsState(allPublishingParams);
      return;
    }

    for (const accountId of accountIdsWithPublishingParams) {
      const wasAccountInPreviousParams = allPublishingParamState[accountId] !== undefined;

      if (wasAccountInPreviousParams && havePublishingParamsChangedForAccountWithId(allPublishingParamState, allPublishingParams, accountId)) {
        if (hasPublishingTransitionedFromPostOrReelOnlyToBoth(allPublishingParamState, allPublishingParams, accountId)) {
          firePlatformSelectionTransitionEvent(accountId, PlatformMediaType.STORIES);
          scrollToAccountSection(accountId, PlatformMediaType.STORIES);
          break;
        }

        if (hasPublishingTransitionedFromStoryOnlyToBoth(allPublishingParamState, allPublishingParams, accountId)) {
          const platformMediaType = allPublishingParams[accountId].POST ? PlatformMediaType.POST : PlatformMediaType.REELS;
          firePlatformSelectionTransitionEvent(accountId, platformMediaType);
          scrollToAccountSection(accountId, platformMediaType);
          break;
        }

        if (hasPublishingTransitionedFromBothToPostOrReelOnly(allPublishingParamState, allPublishingParams, accountId)) {
          const platformMediaType = allPublishingParams[accountId].POST ? PlatformMediaType.POST : PlatformMediaType.REELS;
          firePlatformSelectionTransitionEvent(accountId, platformMediaType);
          scrollToAccountSection(accountId, platformMediaType);
          break;
        }

        if (hasPublishingTransitionedFromBothToStoryOnly(allPublishingParamState, allPublishingParams, accountId)) {
          firePlatformSelectionTransitionEvent(accountId, PlatformMediaType.STORIES);
          scrollToAccountSection(accountId, PlatformMediaType.STORIES);
          break;
        }
      }
    }

    setAllPublishingParamsState(allPublishingParams);
  }, [allPublishingParams]);

  const getMediaTypeFromPublishingParamIndexForAccount = (
    accountId: number,
    index: number,
    updatedPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>
  ): PlatformMediaType => {
    if (isPublishingAsStoryOnly(updatedPublishingParams, accountId)) {
      return PlatformMediaType.STORIES;
    }

    if (isPublishingAsAPostOrReelOnly(updatedPublishingParams, accountId)) {
      if (isPublishingAsPost(updatedPublishingParams, accountId)) {
        return PlatformMediaType.POST;
      }
      return PlatformMediaType.REELS;
    }

    // implies publishing to BOTH
    if (index === 0 && isPublishingAsPost(updatedPublishingParams, accountId)) {
      return PlatformMediaType.POST;
    }

    if (index === 0 && isPublishingAsReel(updatedPublishingParams, accountId)) {
      return PlatformMediaType.REELS;
    }

    return PlatformMediaType.STORIES;
  };

  const onPageScroll = (updatedPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>): void => {
    const scrollingContainer = scrollingContainerRef.current;

    if (!scrollingContainer) {
      return;
    }

    const visibleAreaRect = scrollingContainer.getBoundingClientRect();
    const allWrappers = document.querySelectorAll(`.${publishParamHandlerClass}`);
    const getMaxVisibleElementInfo = (
      wrappers: NodeListOf<Element>,
      updatedPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>
    ): MaxVisiblePublishingParamInfo => {
      let maxVisibleHeight = 0;
      let maxVisibleInfo: MaxVisiblePublishingParamInfo = {accountId: -1, publishingParamIndex: -1, mediaType: PlatformMediaType.POST};

      wrappers.forEach((wrapper) => {
        const accountId = wrapper.getAttribute('data-id-account');
        const {children} = wrapper;

        for (let i = 0; i < children.length; i++) {
          const child = children[i];
          const rect = child.getBoundingClientRect();
          const visibleHeight = Math.min(rect.bottom, visibleAreaRect.bottom) - Math.max(rect.top, visibleAreaRect.top);

          if (visibleHeight > maxVisibleHeight) {
            maxVisibleHeight = visibleHeight;
            const accountIdParsed = accountId ? parseInt(accountId, 10) : -1;
            const paramsIndex = i === 0 ? 0 : 1;
            maxVisibleInfo = {
              accountId: accountIdParsed,
              publishingParamIndex: paramsIndex,
              mediaType: getMediaTypeFromPublishingParamIndexForAccount(accountIdParsed, paramsIndex, updatedPublishingParams),
            };
          }
        }
      });

      return maxVisibleInfo;
    };

    const maxVisibleElementInfo = getMaxVisibleElementInfo(allWrappers, updatedPublishingParams);

    if (maxVisibleElementInfo.accountId !== -1) {
      setSelectedAccountId(maxVisibleElementInfo.accountId);
      setSelectedPublishingParamMediaType(maxVisibleElementInfo.mediaType);
    }
  };

  const getActionMode = (): PostDetailsActionMode => {
    if (wizardStep === SocialMediaWizardStep.ADD_DETAILS) {
      if (wizardPost?.publishOn && wizardPost.timeZoneOffset !== null && wizardPost.state === PostState.SCHEDULE_READY) {
        return PostDetailsActionMode.SCHEDULE;
      }

      return PostDetailsActionMode.PUBLISH;
    }

    return PostDetailsActionMode.NAVIGATE;
  };

  const isAccountWithIdOnlyPublishingToSingleMediaType = (accountId: SocialMediaAccountId): boolean => {
    return !(isPublishingAsPostOrReel(allPublishingParams, accountId) && isPublishingAsStory(allPublishingParams, accountId));
  };

  const getPublishingParamContainerForPlatformMediaType = (parentElement: Element, platformMediaType: PlatformMediaType): Element | null => {
    return parentElement.querySelector(`div[data-platform-media-type="${platformMediaType}"]`);
  };

  const scrollToAccountSection = (accountId: SocialMediaAccountId, platformMediaType: PlatformMediaType): void => {
    const scrollingContainer = scrollingContainerRef.current;

    if (!scrollingContainer) {
      return;
    }

    const allParams = document.querySelectorAll(`.${publishParamHandlerClass}`);
    const firstParamId = allParams[0].getAttribute('data-id-account');

    if (firstParamId) {
      const firstAccId = parseInt(firstParamId, 10);

      if (firstAccId === accountId && isAccountWithIdOnlyPublishingToSingleMediaType(accountId)) {
        scrollContainerToTop(scrollingContainer);
        return;
      }
    }

    const element = document.querySelectorAll(`.${publishParamHandlerClass}[data-key="${getKeyForPublishParams(accountId)}"]`)[0];

    let publishingParamContainerForMediaType = getPublishingParamContainerForPlatformMediaType(element, platformMediaType);

    publishingParamContainerForMediaType ??= element;

    scrollToPublishParams(scrollingContainer, publishingParamContainerForMediaType);
  };

  const onAddAccountClick = (): void => {
    if (isEligibleForSocialAccountsUpsellDialog(numParentAccountsConnected)) {
      openSocialAccountsUpsellDialog();
      return;
    }

    openAddMoreAccountsModal();
  };

  const getTerminatingContainerMargin = (): string => {
    const actionMode = getActionMode();

    if (isExtraSmallScreen || isSmallScreen) {
      if (actionMode === PostDetailsActionMode.SCHEDULE) {
        return `spacing-m-b-13`;
      }
      return ``;
    }
    return styles.lastPublishParamsContainer;
  };

  useEffect(() => {
    const scrollingContainer = scrollingContainerRef.current;

    if (!scrollingContainer) {
      return undefined;
    }

    const handleScroll = (): void => {
      onPageScroll(allPublishingParams);
    };

    scrollingContainer.addEventListener('scroll', handleScroll, {passive: true});

    return (): void => {
      scrollingContainer.removeEventListener('scroll', handleScroll);
    };
  }, [allPublishingParams]);

  const getSocialProfileIconForAccountWithMediaType = (account: SocialAccount, platformMediaType: PlatformMediaType): ReactElement => {
    return (
      <RailAccountProfileIcon
        account={account}
        size={getRailProfileIconSize()}
        platformMediaType={platformMediaType}
        canRemoveAccount={!isMobileRail() && isInEditingState()}
        onSelect={(): void => {
          scrollToAccountSection(account.id, platformMediaType);
        }}
      />
    );
  };

  const isRailTabSelectedForMediaType = (account: SocialAccount, platformMediaType: PlatformMediaType): boolean => {
    if (!selectedAccountId || selectedAccountId !== account.id) {
      return false;
    }

    const isStorySelected = selectedPublishingParamMediaType === PlatformMediaType.STORIES;

    if ((platformMediaType === PlatformMediaType.STORIES) === isStorySelected) {
      return true;
    }

    const doesSelectedTabStillExist = isStorySelected ? isPublishingAsStory(allPublishingParams, account.id) : isPublishingAsPostOrReel(allPublishingParams, account.id);

    return !doesSelectedTabStillExist;
  };

  const isPostRailTabSelected = (account: SocialAccount): boolean => {
    return isRailTabSelectedForMediaType(account, PlatformMediaType.POST);
  };

  const isStoryRailTabSelected = (account: SocialAccount): boolean => {
    return isRailTabSelectedForMediaType(account, PlatformMediaType.STORIES);
  };

  const getProfileIconsForAccountId = (account: SocialAccount): ReactElement => {
    const containerForAccounts: ReactElement[] = [];
    const isPost = isPublishingAsPost(allPublishingParams, account.id);

    if (isPublishingAsPostOrReel(allPublishingParams, account.id)) {
      const isPublishError = hasPublishingErroredOutForAccountWithMediaType(account.id, isPost ? PlatformMediaType.POST : PlatformMediaType.REELS);

      containerForAccounts.push(
        <div
          className={`${styles.accountTab} ${isPostRailTabSelected(account) ? styles.tabSelected : ''} ${isPublishError ? styles.tabDanger : ''}`}
          key={`social-profile-icon-${account.id}-post`}
        >
          {getSocialProfileIconForAccountWithMediaType(account, isPublishingAsPost(allPublishingParams, account.id) ? PlatformMediaType.POST : PlatformMediaType.REELS)}
        </div>
      );
    }

    if (isPublishingAsStory(allPublishingParams, account.id)) {
      const isPublishError = hasPublishingErroredOutForAccountWithMediaType(account.id, PlatformMediaType.STORIES);

      containerForAccounts.push(
        <div
          className={`${styles.accountTab} ${isStoryRailTabSelected(account) ? styles.tabSelected : ''} ${isPublishError ? styles.tabDanger : ''}`}
          key={`social-profile-icon-${account.id}-story`}
        >
          {getSocialProfileIconForAccountWithMediaType(account, PlatformMediaType.STORIES)}
        </div>
      );
    }

    return <Fragment key={`account-tabs-${account.id}`}>{containerForAccounts}</Fragment>;
  };

  const hasPublishingErroredOutForAccountWithMediaType = (accountId: SocialMediaAccountId, platformMediaType: PlatformMediaType): boolean => {
    const mediaTypeLoadingStates = publishingStatus[accountId];
    const loadingStateForPlatform = mediaTypeLoadingStates[platformMediaType];

    return loadingStateForPlatform === LoadingStates.NOT_LOADED;
  };

  const getAddAccountPremiumCrownChip = (): ReactElement | null => {
    if (!isEligibleForSocialAccountsUpsellDialog(numParentAccountsConnected)) {
      return null;
    }

    return <PremiumCrownChip />;
  };

  const getPublishingParamsContainerClasses = (containerIndex: number): string => {
    return `
      ${containerIndex === 1 ? publishParamsStyles.firstPublishingParamContainer : ''} 
      ${containerIndex === nAccounts ? getTerminatingContainerMargin() : getPublishingParamsContainerMarginClass(containerQuery)} 
      ${publishParamHandlerClass}
    `;
  };

  const isMobileRail = (): boolean => {
    return size === SocialMediaWizardContentSize.SMALL || size === SocialMediaWizardContentSize.XSMALL;
  };

  const isInEditingState = (): boolean => {
    return wizardStep === SocialMediaWizardStep.ADD_DETAILS && publishPhase === SocialMediaPublishPhase.NOT_STARTED;
  };

  const hasReachedRailTabLimit = (numRailTabsAdded: number): boolean => {
    return !isMobileRail() && numRailTabsAdded >= MAX_ACCOUNT_BUTTONS_TO_SHOW;
  };

  const getRailProfileIconSize = (): SocialProfileIconSize => {
    return SocialProfileIconSize.LARGE;
  };

  const getRailAddAccountIconClasses = (): string => {
    return isMobileRail() ? styles.mobileRailAddAccount : '';
  };

  let hasAppendedPlaceholder = false;
  let numButtonsHidden = 0;
  const nAccounts = accountIdsWithPublishingParams.length;
  let counter = 0;

  accounts.forEach((account) => {
    if (accountIdsWithPublishingParams.includes(account.id)) {
      counter += 1;
      publishingDetails.push(
        <div
          key={getKeyForPublishParams(account.id)}
          className={getPublishingParamsContainerClasses(counter)}
          data-id-account={account.id}
          data-key={getKeyForPublishParams(account.id)}
        >
          <SocialMediaPostPublishParamsWrapper size={size} key={`id-account-${account.id}`} account={account} />
        </div>
      );

      if (hasReachedRailTabLimit(selectedAccountsTabs.length)) {
        numButtonsHidden += 1;
        hasAppendedPlaceholder = true;
      } else {
        const profileIcons = getProfileIconsForAccountId(account);
        selectedAccountsTabs.push(profileIcons);
      }
    }
  });

  if (hasAppendedPlaceholder) {
    selectedAccountsTabs.push(
      <SocialProfileIconPlaceholder
        key={`social-account-icon-placeholder-${counter}`}
        className={`spacing-m-l-1 spacing-m-b-4 ${styles.railOverflowPlaceholder}`}
        countToShow={numButtonsHidden}
        size={getRailProfileIconSize()}
      />
    );
  }

  if (isInEditingState()) {
    selectedAccountsTabs.push(
      <div key={`add-account-icon-${counter + 1}`} className={styles.addAccountControl}>
        <AddAccountIcon className={getRailAddAccountIconClasses()} size={IconSize.SIZE_ICON_20} onClick={onAddAccountClick} />
        {getAddAccountPremiumCrownChip()}
      </div>
    );
  }

  const renderPreparingDesignState = (): ReactElement | null => {
    if (publishingAction !== SocialPostPublishingAction.PUBLISH || graphicsReadiness !== LoadingStates.LOADING) {
      return null;
    }

    return (
      <div data-testid={SocialMediaTestId.WIZARD_PREPARING_DESIGN_GATE} className="flex-h-row flex-align-items-center flex-justify-center spacing-p-4">
        <CircularProgressLoader size={LOADER_SIZE.SMALL} color={LOADER_COLOR.PRIMARY} classes="spacing-m-l-0 spacing-m-r-2" />
        <Text val={window.i18next.t('pmwjs_preparing_your_design')} size={TextSize.XSMALL} />
      </div>
    );
  };

  return (
    <div>
      <div
        id="account-tabs-onboarding-container"
        className={`
          ${styles.accountTabsContainer}
          ${BLURABLE_CLASS_IN_AI_CAPTION_MODE}
          ${isMobileRail() ? styles.mobileRail : ''}
          ${publishPhase === SocialMediaPublishPhase.IN_FLIGHT ? styles.accountTabsFrozen : ''}
        `}
      >
        {selectedAccountsTabs}
      </div>
      {renderPreparingDesignState()}
      {publishingDetails}
    </div>
  );
}
